import Keycloak, {
  type KeycloakConfig,
  type KeycloakInitOptions,
  type KeycloakLoginOptions,
  type KeycloakLogoutOptions,
  type KeycloakTokenParsed,
} from "keycloak-js";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  getRuntimeConfig,
  type AdaptRuntimeConfig,
} from "./runtimeConfig";

export interface AuthUser {
  subject: string;
  username: string;
  email: string;
  displayName: string;
  roles: string[];
  tokenParsed?: KeycloakTokenParsed;
}

export interface AuthContextValue {
  initialized: boolean;
  authenticated: boolean;
  error: Error | null;
  user: AuthUser | null;
  getToken: () => Promise<string>;
  hasRole: (role: string) => boolean;
  login: (options?: KeycloakLoginOptions) => Promise<void>;
  logout: (options?: KeycloakLogoutOptions) => Promise<void>;
}

type KeycloakClient = Keycloak;

export interface KeycloakAuthProviderProps {
  children: ReactNode;
  config?: AdaptRuntimeConfig;
  defaultLoginOptions?: KeycloakLoginOptions;
  defaultLogoutOptions?: KeycloakLogoutOptions;
  initOptions?: KeycloakInitOptions;
  keycloakFactory?: (config: KeycloakConfig) => KeycloakClient;
  loginRedirectPath?: string;
  logoutRedirectPath?: string;
  onAuthError?: (error: unknown) => void;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const extractRoles = (tokenParsed?: KeycloakTokenParsed): string[] => {
  const realmRoles = tokenParsed?.realm_access?.roles ?? [];
  const resourceAccess = (tokenParsed?.resource_access ?? {}) as Record<
    string,
    { roles?: string[] }
  >;
  const clientRoles = Object.values(resourceAccess).flatMap(
    (resource) => resource.roles ?? [],
  );
  return Array.from(new Set([...realmRoles, ...clientRoles]));
};

export const buildAuthUser = (
  tokenParsed?: KeycloakTokenParsed,
): AuthUser | null => {
  if (!tokenParsed?.sub) return null;

  const givenName =
    typeof tokenParsed.given_name === "string" ? tokenParsed.given_name : "";
  const familyName =
    typeof tokenParsed.family_name === "string" ? tokenParsed.family_name : "";
  const username =
    typeof tokenParsed.preferred_username === "string" &&
    tokenParsed.preferred_username.trim().length > 0
      ? tokenParsed.preferred_username
      : tokenParsed.sub;
  const email =
    typeof tokenParsed.email === "string" && tokenParsed.email.trim().length > 0
      ? tokenParsed.email
      : username;
  const displayName =
    typeof tokenParsed.name === "string" && tokenParsed.name.trim().length > 0
      ? tokenParsed.name
      : `${givenName} ${familyName}`.trim() || username;

  return {
    subject: tokenParsed.sub,
    username,
    email,
    displayName,
    roles: extractRoles(tokenParsed),
    tokenParsed,
  };
};

const toRedirectUri = (path: string): string => {
  if (/^https?:\/\//i.test(path)) return path;
  const origin = globalThis.location?.origin ?? "";
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${origin}${cleanPath}`;
};

const hasAuthCallbackParams = (): boolean => {
  const location = globalThis.location;
  if (!location) return false;

  const query = new URLSearchParams(location.search);
  const hash = new URLSearchParams(
    location.hash.startsWith("#") ? location.hash.slice(1) : location.hash,
  );
  const callbackKeys = ["code", "state", "session_state", "error"];

  return callbackKeys.some((key) => query.has(key) || hash.has(key));
};

export const resolveKeycloakInitOptions = (
  initOptions?: KeycloakInitOptions,
  authCallbackPresent = hasAuthCallbackParams(),
): KeycloakInitOptions => {
  const resolvedInitOptions: KeycloakInitOptions = {
    pkceMethod: "S256",
    silentCheckSsoFallback: false,
    ...initOptions,
    checkLoginIframe: false,
  };

  if (authCallbackPresent && resolvedInitOptions.onLoad === undefined) {
    resolvedInitOptions.onLoad = "check-sso";
  }

  return resolvedInitOptions;
};

export const resolveKeycloakAuthenticated = (
  keycloak: Pick<KeycloakClient, "authenticated" | "token" | "tokenParsed">,
  initAuthenticated: boolean,
): boolean =>
  initAuthenticated ||
  keycloak.authenticated === true ||
  (typeof keycloak.token === "string" && keycloak.token.trim().length > 0) ||
  (typeof keycloak.tokenParsed?.sub === "string" &&
    keycloak.tokenParsed.sub.trim().length > 0);

interface UseKeycloakInitOptions {
  config: AdaptRuntimeConfig;
  initOptions?: KeycloakInitOptions;
  keycloakFactory: (cfg: KeycloakConfig) => KeycloakClient;
  onAuthError?: (error: unknown) => void;
}

function useKeycloakInit({
  config, initOptions, keycloakFactory, onAuthError,
}: UseKeycloakInitOptions) {
  const keycloakRef = useRef<KeycloakClient | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);

  const syncAuthState = useCallback((keycloak: KeycloakClient, isAuth: boolean) => {
    setAuthenticated(isAuth);
    setUser(isAuth ? buildAuthUser(keycloak.tokenParsed) : null);
  }, []);

  const handleError = useCallback((authError: unknown) => {
    const normalisedError = authError instanceof Error ? authError : new Error("Keycloak authentication failed.");
    setError(normalisedError);
    onAuthError?.(authError);
  }, [onAuthError]);

  useEffect(() => {
    let mounted = true;
    const keycloak = keycloakFactory({ url: config.keycloakUrl, realm: config.keycloakRealm, clientId: config.keycloakClientId });
    keycloakRef.current = keycloak;
    keycloak.onAuthSuccess        = () => syncAuthState(keycloak, true);
    keycloak.onAuthRefreshSuccess = () => syncAuthState(keycloak, true);
    keycloak.onAuthLogout         = () => syncAuthState(keycloak, false);
    keycloak.onAuthError          = handleError;
    keycloak.onTokenExpired       = () => { keycloak.updateToken(30).catch((e) => { handleError(e); syncAuthState(keycloak, false); }); };
    keycloak.init(resolveKeycloakInitOptions(initOptions))
      .then((isAuth) => {
        if (!mounted) return;
        const resolved = resolveKeycloakAuthenticated(keycloak, isAuth);
        if (resolved && !keycloak.authenticated) keycloak.authenticated = true;
        syncAuthState(keycloak, resolved);
      })
      .catch((e: unknown) => { if (mounted) handleError(e); })
      .finally(() => { if (mounted) setInitialized(true); });
    return () => { mounted = false; };
  }, [config.keycloakClientId, config.keycloakRealm, config.keycloakUrl, handleError, initOptions, keycloakFactory, syncAuthState]);

  return { keycloakRef, initialized, authenticated, error, user, setUser };
}

async function refreshAndGetToken(
  keycloakRef: React.MutableRefObject<KeycloakClient | null>,
  setUser: React.Dispatch<React.SetStateAction<AuthUser | null>>,
): Promise<string> {
  const keycloak = keycloakRef.current;
  if (!keycloak?.authenticated) throw new Error("No authenticated Keycloak session is available.");
  const refreshed = await keycloak.updateToken(30);
  if (!keycloak.token) throw new Error("Keycloak did not return an access token.");
  if (refreshed) {
    const parsed = keycloak.tokenParsed;
    setUser((prev) => (prev?.subject === parsed?.sub ? prev : buildAuthUser(parsed)));
  }
  return keycloak.token;
}

export const KeycloakAuthProvider = ({
  children, config: providedConfig, defaultLoginOptions, defaultLogoutOptions,
  initOptions, keycloakFactory = (cfg) => new Keycloak(cfg),
  loginRedirectPath, logoutRedirectPath, onAuthError,
}: KeycloakAuthProviderProps): React.JSX.Element => {
  const config = providedConfig ?? getRuntimeConfig();
  const { keycloakRef, initialized, authenticated, error, user, setUser } =
    useKeycloakInit({ config, initOptions, keycloakFactory, onAuthError });

  const login  = useCallback(async (options: KeycloakLoginOptions = {}) => {
    await keycloakRef.current?.login({ ...defaultLoginOptions, redirectUri: toRedirectUri(loginRedirectPath ?? config.loginRedirectPath), ...options });
  }, [config.loginRedirectPath, defaultLoginOptions, loginRedirectPath]);

  const logout = useCallback(async (options: KeycloakLogoutOptions = {}) => {
    await keycloakRef.current?.logout({ ...defaultLogoutOptions, redirectUri: toRedirectUri(logoutRedirectPath ?? config.logoutRedirectPath), ...options });
  }, [config.logoutRedirectPath, defaultLogoutOptions, logoutRedirectPath]);

  const getToken = useCallback(() => refreshAndGetToken(keycloakRef, setUser), [keycloakRef, setUser]);
  const hasRole  = useCallback((role: string): boolean => user?.roles.includes(role) ?? false, [user]);
  const value    = useMemo(
    () => ({ initialized, authenticated, error, user, getToken, hasRole, login, logout }),
    [initialized, authenticated, error, user, getToken, hasRole, login, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * User information supplied from an external auth provider to AuthBridgeProvider.
 */
export interface AuthBridgeUser {
  sub: string;
  email?: string;
  displayName?: string;
  username?: string;
  roles?: string[];
}

export interface AuthBridgeProviderProps {
  children: ReactNode;
  /**
   * Token accessor from the host auth system (e.g. oidc-client-ts via
   * shell-core). Called before every authenticated API request.
   */
  getToken: () => Promise<string>;
  /** Whether the user is currently authenticated. Defaults to true. */
  isAuthenticated?: boolean;
  /** Display-only user information. Does not affect token retrieval. */
  user?: AuthBridgeUser | null;
}

/**
 * Provides the adapt-core-ui AuthContext from an external token source.
 *
 * Use this in host applications that manage their own OIDC session (e.g. via
 * oidc-client-ts) but embed module components that rely on useAuth() or
 * useAuthenticatedAdaptHttpClient() from @heke/adapt-core-ui.
 */
function buildBridgeAuthUser(externalUser: AuthBridgeUser | null | undefined): AuthUser | null {
  if (!externalUser) return null;
  return {
    subject: externalUser.sub,
    username: externalUser.username ?? externalUser.email ?? externalUser.sub,
    email: externalUser.email ?? "",
    displayName: externalUser.displayName ?? externalUser.sub,
    roles: externalUser.roles ?? [],
  };
}

export const AuthBridgeProvider = ({
  children, getToken, isAuthenticated = true, user: externalUser,
}: AuthBridgeProviderProps): React.JSX.Element => {
  const value = useMemo((): AuthContextValue => {
    const authUser = buildBridgeAuthUser(externalUser);
    return {
      initialized: true,
      authenticated: isAuthenticated,
      error: null,
      user: authUser,
      getToken,
      hasRole: (role: string) => authUser?.roles.includes(role) ?? false,
      login: async () => {},
      logout: async () => {},
    };
  }, [getToken, isAuthenticated, externalUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useOptionalAuth = (): AuthContextValue | undefined =>
  useContext(AuthContext);

export const useAuth = (): AuthContextValue => {
  const context = useOptionalAuth();
  if (!context) {
    throw new Error("useAuth must be used inside KeycloakAuthProvider.");
  }
  return context;
};
